import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Button,
  Card,
  CardContainer,
  Colors,
  getSpacing,
  ItemCard,
  ListEmpty,
  Text,
  TextView,
  useAlert,
} from '@rahtab/ui';
import { ListScreen, StackScreenProps } from '@rahtab/stack-navigator';
import PendingIcon from '@rahtab/ui/dist/icons/PendingIcon';
import { useLoadRevisionList } from 'hooks/InventoryHooks/useLoadRevisionList';
import { RootStackType } from '../../Router';
import { useLoad } from 'hooks/InventoryHooks/useLoad';
import RevisionHistoryIcon from '@rahtab/ui/dist/icons/ListEmptyIcons/RevisionHistory';
import moment from 'moment-jalaali';
import { useEffect } from 'react';
import { setCorrectionsLoading } from 'rahtab-redux/slices/correctionListSlice';
import { useSelector, useAppDispatch } from 'rahtab-redux';

const ControlInformation: React.VFC<ControlInformationProps> = ({
  percent,
  title,
  value,
  unitText,
  margin = 'default',
}) => {
  return (
    <View style={StyleSheet.flatten([controlInformationStyles.container])}>
      <View style={controlInformationStyles.descriptionContainer}>
        <PendingIcon
          fill={Colors.secondary}
          style={StyleSheet.flatten([
            controlInformationStyles.dot,
            { marginBottom: margin === 'none' ? 0 : getSpacing() },
          ])}
        />
        <Text margin={margin} color="description">
          {title}:{' '}
          <Text margin={margin}>
            {isNaN(value) ? 0 : value} {unitText}
          </Text>
        </Text>
      </View>
      <Text color="primary" margin={margin}>
        ٪{isNaN(percent) ? 0 : percent}
      </Text>
    </View>
  );
};

interface ControlInformationProps {
  title: string;
  value: number;
  unitText: string;
  percent: number;
  margin?: 'default' | 'none';
}

const controlInformationStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  descriptionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dot: {
    marginRight: getSpacing(0.5),
  },
});

const FixLoadAmount: React.VFC<FixLoadAmountProps> = ({ route, navigation }) => {
  const { assignFinalCode } = route.params;
  const { navigate } = navigation;
  const dispatch = useAppDispatch();
  const load = useLoad({ assignFinalCode: assignFinalCode! });
  const { loading: correctionLoading } = useSelector((state) => state.correctionList);
  const { loading, refetch, called, data } = useLoadRevisionList(assignFinalCode!);
  const alert = useAlert();
  const isRevisionBtnEnabled = load.data?.statusCode === '4' || load.data?.statusCode === '3';
  useEffect(() => {
    if (correctionLoading === 'refreshing') {
      refetch();
      dispatch(setCorrectionsLoading('idle'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctionLoading]);
  return (
    <ListScreen
      title="اصلاح موجودی بار دریافتی"
      loading={loading || (!load.called && load.loading)}
      refreshing={(called && loading) || (load.called && load.loading)}
      onRefresh={refetch}
      data={data}
      ListEmptyComponent={<ListEmpty text="سابقه اصلاح موجودی وجود ندارد." imageComponent={<RevisionHistoryIcon />} />}
      listData={data?.correctionList}
      renderControls={({
        correctionInfo: {
          conflictionAmount,
          conflictionPercent,
          defectiveAmount,
          defectivePrecent,
          unitText,
          otherReasonAmount,
          otherReasonPercent,
          outSaleAmount,
          outSalePercent,
          correctionsCount,
        },
      }) => {
        return (
          <>
            {correctionsCount !== 0 && (
              <Card style={styles.controlCard}>
                <CardContainer>
                  <ControlInformation
                    title="مغایرت اطلاعات بار"
                    percent={conflictionPercent!}
                    value={conflictionAmount!}
                    unitText={unitText!}
                  />
                  <ControlInformation
                    title="افت یا معیوب شدن بار"
                    percent={defectivePrecent!}
                    value={defectiveAmount!}
                    unitText={unitText!}
                  />
                  <ControlInformation
                    title="فروش خارج از شبکه"
                    percent={outSalePercent!}
                    value={outSaleAmount!}
                    unitText={unitText!}
                  />
                  <ControlInformation
                    title="دلایل دیگر"
                    percent={otherReasonPercent!}
                    value={otherReasonAmount!}
                    unitText={unitText!}
                    margin="none"
                  />
                </CardContainer>
              </Card>
            )}
            <View style={styles.controlBottomView}>
              <TextView
                title="تعداد اصلاحیه‌ها"
                value={correctionsCount}
                isSpaceBetween
                style={styles.textViewInControl}
              />
              <Button
                color="primary"
                disabled={!isRevisionBtnEnabled}
                text="ثبت اصلاح موجودی جدید"
                onPress={() =>
                  alert({
                    message:
                      'برای اصلاح موجودی، عرضه فعال موجود روی اين بار متوقف می شود. آیا مايل به ادامه عمليات می باشيد؟',
                    buttons: [
                      {
                        color: 'primary',
                        text: 'تایید',
                        variant: 'contained',
                        onPress: () => navigate('SubmitNewFix', { assignFinalCode }),
                      },
                    ],
                  })
                }
                style={styles.newFixButton}
              />
            </View>
          </>
        );
      }}
      keyExtractor={(_, index) => `item-${index}`}
      renderItem={({ item }) => {
        const { amount, unitText, reason, submitDate } = item;
        return (
          <ItemCard
            hasChevron={false}
            caption={
              <Text margin="none" color="description">
                زمان ثبت: <Text margin="none">{moment(submitDate).format('HH:mm | jYYYY/jMM/jDD')}</Text>
              </Text>
            }>
            <Text color="description">
              اصلاح موجودی ناشی از
              <Text>{reason}</Text>
            </Text>
            <Text margin="none" color="description">
              مقدار:{' '}
              <Text margin="none">
                {amount} {unitText}
              </Text>
            </Text>
          </ItemCard>
        );
      }}
    />
  );
};

export default FixLoadAmount;

const styles = StyleSheet.create({
  controlCard: {
    marginVertical: getSpacing(),
  },
  controlBottomView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  textViewInControl: {
    flex: 1,
    marginBottom: 0,
  },
  newFixButton: {
    flex: 2,
    marginLeft: getSpacing(),
  },
});
type FixLoadAmountProps = StackScreenProps<RootStackType, 'FixLoadAmount'>;
